import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isEmptyWidgetData, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, getEChartsTooltipTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export type EarningInterval = 'annual' | 'quarterly';

interface EarningWidgetConfig extends WidgetConfig {
  interval?: EarningInterval;
  dataPoints?: number;
}

export class TraderverseEarning {

  private mapEarningChartData(data: any[]): Array<{ name: string; data: { x: any; y: any[] }[] }> {
    const chartDatalist: Array<{ name: string; data: { x: any; y: any[] }[] }> = [
      { name: "Estimated EPS", data: [] },
      { name: "Actual EPS (beat)", data: [] },
      { name: "Actual EPS (missed)", data: [] },
      { name: "Actual EPS (neutral)", data: [] },
    ];
    if (Array.isArray(data)) {
      data.forEach((item) => {
        chartDatalist[0].data.push({
          x: item.x,
          y: [item.y[0], item.y[1]],
        });
        if (item.x[1] === "beat") {
          chartDatalist[1].data.push({
            x: item.x,
            y: [item.y[1], item.y[0]],
          });
        } else if (item.x[1] === "missed") {
          chartDatalist[2].data.push({
            x: item.x,
            y: [item.y[1], item.y[0]],
          });
        } else if (item.x[1] === "neutral") {
          chartDatalist[3].data.push({
            x: item.x,
            y: [item.y[1], item.y[0]],
          });
        }
      });
    }
    return chartDatalist;
  }
  private container: HTMLElement;
  private config: EarningWidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private earningInterval: EarningInterval = 'annual';
  private dataPoints: number = 8;

  constructor(config: EarningWidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.earningInterval = this.config.interval ?? 'annual';
    this.dataPoints = Math.min(40, Math.max(1, this.config.dataPoints ?? 8));
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<EarningWidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      interval: 'annual',
      dataPoints: 8
    };
  }

  /** Format date as YYYY-MM-DD in local time (avoids timezone shift from toISOString). */
  private formatDateLocal(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Compute start and end dates from interval and data points. */
  private getDateRangeFromFilters(): { startDate: string; endDate: string } {
    const now = new Date();
    const endDate = this.formatDateLocal(now);
    if (this.earningInterval === 'annual') {
      const start = new Date(now.getFullYear() - this.dataPoints, now.getMonth(), now.getDate());
      const startDate = this.formatDateLocal(start);
      return { startDate, endDate };
    }
    // Quarterly: request extra history so API has enough reported quarters, then we slice to dataPoints.
    const monthsBack = Math.max(this.dataPoints * 3, 12 * 5); // at least 5 years for quarterly
    const start = new Date(now.getFullYear(), now.getMonth() - monthsBack, now.getDate());
    const startDate = this.formatDateLocal(start);
    return { startDate, endDate };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let widgetInstanceCounter = 0;
    const widgetIndex = widgetInstanceCounter++;
    const symbolOptions = this.fetchSymbols()
      .map(s => `<option value="${s.value}">${s.label}</option>`).join('');
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

    const widgetHtml = `
                  <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${this.config.theme}"  data-testing-id="asset-earning-widget-${widgetIndex}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Earnings</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                  <div class="trader-sm-card" id="selected-symbol">
                  ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: "stock", name: "" })}
                  </div>
                  <span id="symbol-dropdown-placeholder"></span>
              </div>`}

              <div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div>

            </div>
          </div>
          <div class="inBody">
          

            <div class="filtersBox sideBar hidden">
              <div class="item">
                <select id="earning-interval" class="dropdown-btn">
                  <option value="annual" ${this.earningInterval === 'annual' ? 'selected' : ''}>Annual</option>
                  <option value="quarterly" ${this.earningInterval === 'quarterly' ? 'selected' : ''}>Quarterly</option>
                </select>
              </div>
              <div class="item" data-tooltip="Data points">
                <input type="number" id="earning-data-points" min="1" max="40" value="${this.dataPoints}">
              </div>
            </div>

            <div class="chartDv earningChartScatter" id="earningContainer" style="width:100%;height:280px;">
            </div>

          </div>
        </div>

    `;
    this.shadowRoot.innerHTML = widgetHtml;
    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    dropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;

    const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
    if (filtersToggle && filtersBox) {
      filtersToggle.addEventListener('click', () => {
        filtersBox.classList.toggle('hidden');
      });
    }

    const intervalSelect = this.shadowRoot.getElementById('earning-interval') as HTMLSelectElement;
    intervalSelect?.addEventListener('change', () => {
      this.earningInterval = (intervalSelect.value || 'annual') as EarningInterval;
      const symbol = this.config.symbol ?? this.fetchSymbols()[0].value;
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) widget.classList.add('loading');
      this.setupChart(symbol).then(() => {
        if (widget) widget.classList.remove('loading');
      });
    });

    const dataPointsInput = this.shadowRoot.getElementById('earning-data-points') as HTMLInputElement;
    dataPointsInput?.addEventListener('change', () => {
      const val = parseInt(dataPointsInput.value, 10);
      if (!isNaN(val)) {
        this.dataPoints = Math.min(40, Math.max(1, val));
        dataPointsInput.value = String(this.dataPoints);
        const symbol = this.config.symbol ?? this.fetchSymbols()[0].value;
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        this.setupChart(symbol).then(() => {
          if (widget) widget.classList.remove('loading');
        });
      }
    });
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async fetchUpcomingEarningsData(symbol: string, startDate: string, endDate: string, interval: EarningInterval): Promise<any[]> {
    const payload = {
      symbol,
      start_date: startDate,
      end_date: endDate,
      interval
    };

    try {
      const response = await apiGet({
        method: 'POST',
        url: `${APIURL}/v1/earning/chart`,
        body: payload,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch upcoming earnings data:', error);
      return [];
    }
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      const { startDate, endDate } = this.getDateRangeFromFilters();
      let earningsData = await this.fetchUpcomingEarningsData(symbol, startDate, endDate, this.earningInterval);
      if (Array.isArray(earningsData) && earningsData.length > this.dataPoints) {
        earningsData = earningsData.slice(-this.dataPoints);
      }

      const chartSeries = this.mapEarningChartData(earningsData);

      this.currentData = {
        symbol,
        earnings: earningsData,
        chartSeries,
          hasData: Array.isArray(earningsData) && earningsData.length > 0

      };

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }

  private updateDisplay(): void {
    const container = this.shadowRoot.querySelector('#earningContainer') as HTMLElement;
    if (!container) return;
    if (this.currentData == null) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (isEmptyWidgetData(this.currentData?.earnings)) {
      container.removeAttribute('data-testing-id');
      container.innerHTML = getNoDataHTML();
      return;
    }
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderAnalystRating();
  }

 private renderAnalystRating(): void {
  const container = this.shadowRoot.querySelector('#earningContainer') as HTMLElement;
  if (!container) return;

  const hasData = this.currentData?.earnings?.length > 0;

  if (hasData) {
    container.setAttribute('data-testing-id', 'earnings-chart');
  } else {
    container.removeAttribute('data-testing-id');
  }

  this.drawChart();
}

  /** Y-axis bounds aligned to EPS-style steps (matches tradersgpt-web02 EarningsChart). */
  private getEpsAxisBounds(values: number[]): { min: number; max: number } {
    const nums = values.filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (!nums.length) return { min: 0, max: 1 };
    let lo = Math.min(...nums);
    let hi = Math.max(...nums);
    const dataMin = lo;
    if (lo === hi) {
      lo -= 0.05;
      hi += 0.05;
    }
    const span = hi - lo;
    const pad = Math.max(span * 0.12, 0.05);
    lo -= pad;
    hi += pad;
    const step = 0.05;
    let min = Math.floor(lo / step) * step;
    const max = Math.ceil(hi / step) * step;
    // Keep axis at/above 0 when all EPS values are non-negative (avoids $-0.10 padding)
    if (dataMin >= 0) min = Math.max(0, min);
    return { min, max };
  }

  private isLightEarningsTheme(): boolean {
    return this.config.theme === 'white';
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#earningContainer') as HTMLElement;
    if (!container) return;

    if (echarts.getInstanceByDom(container)) {
      echarts.dispose(container);
    }

    const earnings = this.currentData && Array.isArray(this.currentData.earnings)
      ? this.currentData.earnings
      : [];
    const categories = earnings.map((item: any) => (item.x && item.x[0] ? String(item.x[0]) : ''));
    const statuses = earnings.map((item: any) => (item.x && item.x[1] ? String(item.x[1]).toLowerCase() : ''));

    const allY: number[] = [];
    const estimatedPts: [string, number][] = [];
    const beatPts: [string, number][] = [];
    const missedPts: [string, number][] = [];
    const neutralPts: [string, number][] = [];

    earnings.forEach((item: any) => {
      const q = item.x && item.x[0] ? String(item.x[0]) : '';
      const est = item.y && item.y[0] !== undefined && item.y[0] !== null ? Number(item.y[0]) : NaN;
      const act = item.y && item.y[1] !== undefined && item.y[1] !== null ? Number(item.y[1]) : NaN;
      const st = item.x && item.x[1] ? String(item.x[1]).toLowerCase() : '';
      if (!Number.isNaN(est)) {
        estimatedPts.push([q, est]);
        allY.push(est);
      }
      if (!Number.isNaN(act)) {
        allY.push(act);
        if (st === 'beat') beatPts.push([q, act]);
        else if (st === 'missed') missedPts.push([q, act]);
        else neutralPts.push([q, act]);
      }
    });

    const { min: yMin, max: yMax } = this.getEpsAxisBounds(allY.length ? allY : [0, 1]);
    const light = this.isLightEarningsTheme();
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const axisMuted = light ? '#656B45' : 'rgba(255,255,255,0.55)';
    const axisLine = light ? 'rgba(0,0,0,0.35)' : '#ffffff';
    const splitLine = light ? 'rgba(0,0,0,0.08)' : '#222222';
    const yLabelColor = light ? '#656B45' : '#9eaac0';
    const quarterColor = light ? '#8a8a7a' : '#465787';
    const green = '#06D186';
    const pink = '#FF0066';
    const neutralM = '#9eaac0';

    const option = {
      textStyle: {
        fontFamily: 'Montserrat, Inter, sans-serif',
      },
      grid: {
        left: 52,
        right: 14,
        top: 16,
        bottom: 52,
        containLabel: false,
      },
      tooltip: {
        trigger: 'item',
        ...getEChartsTooltipTheme(widgetBox),
        formatter: (p: any) => {
          const sname = p.seriesName || '';
          const pair = p.value as [string, number];
          if (!pair || pair.length < 2) return '';
          const v = pair[1];
          return `${pair[0]}<br/>${sname}: $${typeof v === 'number' ? v.toFixed(2) : v}`;
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        boundaryGap: true,
        axisLine: {
          show: true,
          lineStyle: { color: axisLine, width: 1 },
        },
        axisTick: {
          show: true,
          alignWithLabel: true,
          length: 5,
          lineStyle: { color: axisLine, width: 1 },
        },
        axisLabel: {
          interval: 0,
          margin: 14,
          color: axisMuted,
          formatter: (value: string) => {
            const idx = categories.indexOf(value);
            const st = idx >= 0 ? statuses[idx] : '';
            const upper = st === 'beat' ? 'BEAT' : st === 'missed' ? 'MISSED' : st === 'neutral' ? 'NEUTRAL' : '';
            if (!upper) return `{q|${value}}`;
            const tag = st === 'beat' ? 'b' : st === 'missed' ? 'm' : 'n';
            return `{q|${value}}\n{${tag}|${upper}}`;
          },
          rich: {
            q: {
              fontSize: 12,
              fontWeight: 500,
              color: quarterColor,
              lineHeight: 16,
              align: 'center',
            },
            b: {
              fontSize: 12,
              fontWeight: 700,
              color: green,
              lineHeight: 18,
              align: 'center',
            },
            m: {
              fontSize: 12,
              fontWeight: 700,
              color: pink,
              lineHeight: 18,
              align: 'center',
            },
            n: {
              fontSize: 12,
              fontWeight: 700,
              color: neutralM,
              lineHeight: 18,
              align: 'center',
            },
          },
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        min: yMin,
        max: yMax,
        scale: false,
        splitNumber: 6,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { color: splitLine, width: 1, opacity: light ? 1 : 0.35 },
        },
        axisLabel: {
          color: yLabelColor,
          fontSize: 12,
          fontWeight: 600,
          formatter: (v: number) => `$${Number(v).toFixed(2)}`,
        },
      },
      series: [
        {
          name: 'Actual EPS (beat)',
          type: 'scatter',
          data: beatPts,
          symbolSize: 20,
          itemStyle: { color: green },
          emphasis: { scale: 1.15 },
          z: 2,
        },
        {
          name: 'Actual EPS (missed)',
          type: 'scatter',
          data: missedPts,
          symbolSize: 20,
          itemStyle: { color: pink },
          emphasis: { scale: 1.15 },
          z: 3,
        },
        {
          name: 'Actual EPS (neutral)',
          type: 'scatter',
          data: neutralPts,
          symbolSize: 20,
          itemStyle: { color: '#dddddd' },
          emphasis: { scale: 1.15 },
          z: 4,
        },
        {
          name: 'Estimated EPS',
          type: 'scatter',
          data: estimatedPts,
          symbolSize: 20,
          itemStyle: {
            color: 'rgba(0,0,0,0)',
            borderColor: light ? '#333333' : '#ffffff',
            borderWidth: 2,
          },
          emphasis: { scale: 1.15 },
          z: 5,
        },
      ],
      animation: true,
      animationDuration: 200,
    };

    this.currentChart = echarts.init(container);
    this.currentChart.setOption(option);
  }


  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseEarning: typeof TraderverseEarning; } }
if (typeof window !== 'undefined') { window.TraderverseEarning = TraderverseEarning; }
